// Sugalarm — cycle 316: pure step-sequencing for the first-run onboarding
// tour. No DOM, no plugin access — unit-testable derivations only; the
// tour component renders whatever this module says the flow is.
//
// Sequences (the approved mockup's contract):
//   iOS (4):     welcome → notifications → critical → summary
//   Android (6): welcome → notifications → dnd → fullscreen → battery → summary
//   Android < API 34: the fullscreen step (and its progress dot) is omitted
//   entirely, making a 5-step flow.

import type {
  AlarmReliabilityStatus,
  IosAlarmReliabilityStatus,
} from "./alarmReliability";

export type TourPlatform = "ios" | "android";

export type TourStepId =
  | "welcome"
  | "notifications"
  | "critical" // iOS only
  | "dnd" // android only
  | "fullscreen" // android only, API 34+
  | "battery" // android only
  | "summary";

/** Whether the Android full-screen step applies — the same rule the
 *  cycle-315 checklist row uses: API 34+ per `sdkInt`; on an APK too old
 *  to report `sdkInt`, only when the grant is reported actionable. */
export function fullscreenStepApplies(
  s: Pick<AlarmReliabilityStatus, "sdkInt" | "fullScreenIntent"> | null,
): boolean {
  if (s == null) return false;
  if (s.sdkInt != null) return s.sdkInt >= 34;
  return s.fullScreenIntent === false;
}

/** The ordered step list for a platform + status snapshot. */
export function deriveTourSteps(
  platform: TourPlatform,
  status:
    | Pick<AlarmReliabilityStatus, "sdkInt" | "fullScreenIntent">
    | null,
): TourStepId[] {
  if (platform === "ios") {
    return ["welcome", "notifications", "critical", "summary"];
  }
  const steps: TourStepId[] = ["welcome", "notifications", "dnd"];
  if (fullscreenStepApplies(status)) steps.push("fullscreen");
  steps.push("battery", "summary");
  return steps;
}

/** Is a given permission step's grant currently met? (welcome/summary are
 *  never "met" — they aren't permissions.) Optimistic on unknown Android
 *  fields, matching the cycle-315 checklist derivation. */
export function stepGranted(
  step: TourStepId,
  platform: TourPlatform,
  android: AlarmReliabilityStatus | null,
  ios: IosAlarmReliabilityStatus | null,
): boolean {
  if (platform === "ios") {
    if (step === "notifications") return ios?.notificationsEnabled === true;
    if (step === "critical") return ios?.criticalAlertsAllowed === true;
    return false;
  }
  if (android == null) return false;
  switch (step) {
    case "notifications":
      return android.notificationsEnabled ?? true;
    case "dnd":
      return android.dndAccess ?? android.dndPolicyAccess;
    case "fullscreen":
      return android.fullScreenIntent;
    case "battery":
      return android.batteryExempt ?? android.batteryOptimizationsIgnored;
    default:
      return false;
  }
}

/** The permission steps of a flow (welcome/summary excluded). */
export function permissionSteps(steps: TourStepId[]): TourStepId[] {
  return steps.filter((s) => s !== "welcome" && s !== "summary");
}

/** The first permission step whose grant is still unmet — the "Fix now"
 *  jump target. Null when everything is granted. */
export function firstUnmetStep(
  steps: TourStepId[],
  platform: TourPlatform,
  android: AlarmReliabilityStatus | null,
  ios: IosAlarmReliabilityStatus | null,
): TourStepId | null {
  for (const s of permissionSteps(steps)) {
    if (!stepGranted(s, platform, android, ios)) return s;
  }
  return null;
}

/** How many permission steps in the flow are unmet. */
export function unmetStepCount(
  steps: TourStepId[],
  platform: TourPlatform,
  android: AlarmReliabilityStatus | null,
  ios: IosAlarmReliabilityStatus | null,
): number {
  return permissionSteps(steps).filter(
    (s) => !stepGranted(s, platform, android, ios),
  ).length;
}
